import {
  useState,
  useEffect,
  useRef
} from "react";
import {
  useLocation,
  useNavigate,
  Link
} from "react-router-dom";
import {
  verifyForgotOtp
} from "../api/passwordApi";
import {
  KeyRound,
  ArrowLeft,
  HelpCircle,
  AlertCircle,
  Loader2
} from "lucide-react";
import {
  useToast
} from "../context/ToastContext";

const VerifyResetOtp = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email || "j.doe@oakwood-academy.edu";

  // State Management
  const [otpArray, setOtpArray] = useState(new Array(6).fill(""));
  const [loading, setLoading] = useState(false);
  
  const toast = useToast();

  // Timer States
  const [timeLeft, setTimeLeft] = useState(59);
  const [isTimerActive, setIsTimerActive] = useState(true);

  // References for multi-box focus controls
  const inputRefs = useRef([]);

  // Auto-focus first input block on initial mount
  useEffect(() => {
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  // Countdown Interval Side Effect
  useEffect(() => {
    let countdown;
    if (isTimerActive && timeLeft > 0) {
      countdown = setInterval(() => {
        setTimeLeft((prev) => prev - 1);
      }, 1000);
    } else if (timeLeft === 0) {
      setIsTimerActive(false);
    }
    return () => clearInterval(countdown);
  }, [timeLeft, isTimerActive]);

  // Input mutation logic handling focus progression
  const handleChange = (value, index) => {
    const cleanValue = value.replace(/[^0-9]/g, ""); // Restrict to numeric tokens
    if (!cleanValue) return;

    const newOtpArray = [...otpArray];
    // Keep only the last character entered if replacing an existing number
    newOtpArray[index] = cleanValue.substring(cleanValue.length - 1);
    setOtpArray(newOtpArray);

    // Shift window focus forward if space remains available
    if (index < 5 && newOtpArray[index] !== "") {
      inputRefs.current[index + 1].focus();
    }
  };

  // Strategic keystroke management for backspaces
  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace") {
      const newOtpArray = [...otpArray];
      
      if (otpArray[index] !== "") {
        newOtpArray[index] = "";
        setOtpArray(newOtpArray);
      } else if (index > 0) {
        newOtpArray[index - 1] = "";
        setOtpArray(newOtpArray);
        inputRefs.current[index - 1].focus();
      }
    }
  };

  // Intercept paste events for seamless code entry
  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").trim().replace(/[^0-9]/g, "").slice(0, 6);
    
    if (pastedData.length === 6) {
      const splitDigits = pastedData.split("");
      setOtpArray(splitDigits);
      inputRefs.current[5].focus();
    }
  };

  // Handle Code Resend Action
  const handleResend = () => {
    if (isTimerActive) return;
    
    // Trigger your mock/live notification alert sequence or custom endpoints here
    setTimeLeft(59);
    setIsTimerActive(true);
    setOtpArray(new Array(6).fill(""));
    if (inputRefs.current[0]) inputRefs.current[0].focus();
  };

  // Submit Processing Routine
  const handleSubmit = async (e) => {
    e.preventDefault();
    const parsedOtp = otpArray.join("");

    if (parsedOtp.length !== 6) {
      toast.error("Please complete the entry of your 6-digit confirmation key.");
      return;
    }

    try {
      setLoading(true);

      await verifyForgotOtp(email, parsedOtp);

      navigate("/reset-password", {
        state: { email },
      });
    } catch (err) {
      toast.error(
        err?.response?.data?.message || "OTP Verification Failed. Please confirm accuracy and try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-neutral-50 dark:bg-neutral-950 text-neutral-900 dark:text-neutral-100 antialiased">
      
      {/* Structural Header Wrapper */}
      <header className="w-full h-16 bg-white dark:bg-neutral-900 flex justify-between items-center px-6 md:px-10 shadow-sm border-b border-[#c7c4d8]">
        <div className="text-xl md:text-2xl font-bold text-[#3525cd] tracking-tight">
          EduClarify ERP
        </div>
        <div>
          <button className="flex items-center text-[#464555] hover:bg-[#eff4ff] px-4 py-2 rounded-lg transition-colors duration-150 text-sm font-medium">
            <HelpCircle className="w-4 h-4 mr-2" />
            Support
          </button>
        </div>
      </header>

      {/* Main Core Display Segment */}
      <main className="flex-grow flex items-center justify-center p-4 relative overflow-hidden">
        
        {/* Decorative Blurred Gradient Spots */}
        <div className="absolute top-[-10%] right-[-5%] w-96 h-96 bg-[#e2dfff] opacity-30 rounded-full blur-3xl pointer-events-none"></div>
        <div className="absolute bottom-[-5%] left-[-10%] w-80 h-80 bg-[#dce9ff] opacity-40 rounded-full blur-3xl pointer-events-none"></div>

        <div className="w-full max-w-md z-10">
          
          {/* Form Exit Action Link */}
          <Link 
            to="/login" 
            className="inline-flex items-center text-[#3525cd] text-sm font-semibold mb-6 group hover:text-[#2a1daf] transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-1.5 transition-transform group-hover:-translate-x-1" />
            Back to Login
          </Link>

          {/* Core Visual Glass Interface Card */}
          <div className="bg-white/70 dark:bg-neutral-900/70 backdrop-blur-md rounded-xl p-8 shadow-sm border border-neutral-200 dark:border-neutral-800/80">
            
            {/* Context Heading Header */}
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-[#4f46e5] text-white flex items-center justify-center rounded-2xl mx-auto mb-4 animate-[bounce_6s_ease-in-out_infinite]">
                <KeyRound className="w-8 h-8" />
              </div>
              <h1 className="text-xl md:text-2xl font-bold text-neutral-900 dark:text-neutral-100 mb-2">Account Recovery</h1>
              <p className="text-sm leading-relaxed text-[#464555]">
                We've sent a 6-digit verification code to<br />
                <span className="font-bold text-neutral-900 dark:text-neutral-100">{email}</span>
              </p>
            </div>

            {/* Error Message Diagnostic Box */}
            

            {/* Main Operational Processing Form */}
            <form onSubmit={handleSubmit} className="space-y-6">
              
              {/* Box Segmentation Elements Container Layout */}
              <div className="flex justify-between gap-2" onPaste={handlePaste}>
                {otpArray.map((digit, idx) => (
                  <input
                    key={idx}
                    ref={(el) => (inputRefs.current[idx] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleChange(e.target.value, idx)}
                    onKeyDown={(e) => handleKeyDown(e, idx)}
                    className="w-12 h-14 md:w-14 md:h-16 text-center text-xl font-bold bg-neutral-50 dark:bg-neutral-950 border border-[#c7c4d8] rounded-lg focus:outline-none focus:border-[#3525cd] focus:ring-4 focus:ring-[#3525cd]/10 transition-all duration-150"
                    required
                  />
                ))}
              </div>

              {/* Form Operations Button & Resend Configurations */}
              <div className="flex flex-col gap-5">
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#3525cd] text-white font-semibold py-4 rounded-lg shadow-sm hover:bg-[#2a1daf] active:scale-[0.98] transition-all duration-150 flex items-center justify-center disabled:opacity-75 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    "Verify & Proceed"
                  )}
                </button>

                <div className="flex flex-col items-center gap-1.5 text-center">
                  <p className="text-sm text-[#464555]">Didn't receive the code?</p>
                  <button
                    onClick={handleResend}
                    disabled={isTimerActive}
                    type="button"
                    className="text-[#3525cd] text-sm font-semibold hover:underline decoration-2 underline-offset-4 disabled:text-[#777587] disabled:no-underline disabled:cursor-not-allowed transition-colors"
                  >
                    Resend Option
                  </button>
                  {isTimerActive && (
                    <p className="text-xs text-[#565e74] font-medium transition-all">
                      Wait {timeLeft}s to resend
                    </p>
                  )}
                </div>
              </div>

            </form>
          </div>

          {/* Secure Institutional Protocol Layout Details */}
          <div className="mt-8 text-center">
            <p className="text-xs text-[#464555] opacity-70 leading-relaxed">
              This session is secured with end-to-end encryption. <br />
              TFC Security Protocol v2.4.0
            </p>
          </div>

        </div>
      </main>
    </div>
  );
};

export default VerifyResetOtp;